import React, { useMemo } from 'react';
import {Icon, Modal, Text} from '@/ui-kits';
import {ConsultationDoctor, ConsultationHospital, Doctor, FileItem, FileSerializableItem} from '@/interface';
import {SpecialistCard} from '../../dashboard-screen';
import {FilePreview} from '../../common';
import { HospitalTypeLabel, SpecialistEntityType } from '@/constants';
import {Doctor as DoctorInterface} from '@/interface';

interface ConsultationDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  doctor?: ConsultationDoctor;
  hospital_poc?: ConsultationHospital;
  uploadedFiles: FileItem[];
  preUploadedFiles: FileItem[];
}

export const ConsultationDetailsModal: React.FC<ConsultationDetailsModalProps> = ({isOpen, onClose, doctor, hospital_poc, uploadedFiles, preUploadedFiles}) => {
  if (!isOpen) return null;

  const handleView = (file: FileSerializableItem) => {
    // optional custom behavior
  };

  const doctorData = useMemo(() => {
    let data: Partial<DoctorInterface> = {};
    if (doctor){
      data = {
        location: doctor.location,
        email: doctor.email,
        phone: doctor.phone,
        specialization: doctor.specialization,
        rating: doctor.rating,
        experience: doctor.experience,
        image: doctor.image,
        name: doctor.name,
        education: doctor.education,
        id: doctor.id,
        entity_type: SpecialistEntityType.Specialist,
      };
    }
    else if (hospital_poc){
      data = {
        address: hospital_poc.address,
        email: hospital_poc.email,
        phone: hospital_poc.phone,
        specialization: null as any,
        rating: 0,
        image: hospital_poc.image || '',
        name: hospital_poc.name,
        id: hospital_poc.id,
        entity_type: SpecialistEntityType.Hospital,
        hospital_type: {
          id: hospital_poc.type,
          label: HospitalTypeLabel[hospital_poc.type],
        },
      }
    }

    return data;
  }, [doctor, hospital_poc]);

  return (
    <Modal open={isOpen} onClose={onClose} className="rounded-xl p-0 w-[95vw] sm:w-[90vw] md:w-[85vw] lg:w-[75vw] max-w-[900px] max-h-[88vh] bg-white">
      <div className="flex flex-col h-full max-h-[88vh] bg-white rounded-xl overflow-hidden">
        {/* Header - Fixed */}
        <div className="flex-shrink-0 px-4 sm:px-3 pt-4 pb-2 ">
          <div className="flex justify-between items-center">
            <Text variant='titleLarge' className="text-ui_blue">Consultation Details</Text>
            <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0">
              <Icon type="close" className="size-5" />
            </button>
          </div>
        </div>

        {/* Scrollable Content */}
        <div
          className="flex-1 overflow-y-auto px-3 sm:px-4 py-4"
          style={{
            scrollbarWidth: 'thin',
            scrollbarColor: '#CBD5E0 transparent',
          }}>
          <style
            dangerouslySetInnerHTML={{
              __html: `
              .modal-content::-webkit-scrollbar { width: 6px; }
              .modal-content::-webkit-scrollbar-track { background: transparent; }
              .modal-content::-webkit-scrollbar-thumb { background: #CBD5E0; border-radius: 3px; }
              .modal-content::-webkit-scrollbar-thumb:hover { background: #A0AEC0; }
            `,
            }}
          />

          {/* Single Column Layout for Mobile (< 640px) */}
          <div className="flex flex-col gap-4 sm:hidden">
            {/* Doctor Card */}
            <div style={{boxShadow: '1px 4px 8px 2px #E3E4E5'}} className="bg-[#CBEBFB33] flex flex-col border-ui_input_border border rounded-xl p-3 gap-2">
              <Text variant='subtitle' className='text-ui_blue'>{doctor ? "Doctor Profile" : "Hospital Profile"}</Text>
              <div className="flex justify-center">
                <SpecialistCard compact readonly doctor={doctorData as Doctor} />
              </div>
            </div>

            {/* Uploaded Files */}
            <div className="flex flex-col gap-2">
              <Text variant='subtitle' className="text-ui_blue">Uploaded Files</Text>
              <div className="space-y-2">
                {uploadedFiles.map((item, index) => (
                  <FilePreview bgVariant="blue" allowed_actions={[]} key={`uploaded-${index}`} file={item} readonly handleView={handleView} />
                ))}
                {preUploadedFiles.map((item, index) => (
                  <FilePreview allowed_actions={[]} bgVariant="blue" key={`preuploaded-${index}`} file={item} readonly handleView={handleView} />
                ))}
              </div>
            </div>
          </div>

          {/* Two Column Layout for Tablet & Desktop (≥ 640px) */}
          <div className="hidden sm:grid sm:grid-cols-2 gap-4 lg:gap-5">
            {/* Doctor Card */}
            <div
              style={{boxShadow: '1px 4px 8px 2px #E3E4E5'}}
              className="bg-[#CBEBFB33] flex flex-col border-ui_input_border border rounded-xl p-3.5 lg:p-4 gap-2 h-fit">
              <Text variant='subtitle' className='text-ui_blue'>{doctor ? "Doctor Profile" : "Hospital Profile"}</Text>
              <div className="flex justify-center">
                <SpecialistCard compact readonly doctor={doctorData as Doctor} />
              </div>
            </div>

            {/* Uploaded Files */}
            <div className="flex flex-col gap-2.5">
            <Text variant='subtitle' className="text-ui_blue">Uploaded Files</Text>
              <div className="space-y-2">
                {uploadedFiles.map((item, index) => (
                  <FilePreview bgVariant='blue' allowed_actions={[]} key={`uploaded-${index}`} file={item} readonly handleView={handleView} />
                ))}
                {preUploadedFiles.map((item, index) => (
                  <FilePreview bgVariant='blue' allowed_actions={[]} key={`preuploaded-${index}`} file={item} readonly handleView={handleView} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};
